import { CSSProperties } from 'react';
import { GoBackLinkComponent, leftHeaderContainerStyle } from '@vivli/shared/components';
import { Size, Styles } from '@vivli/shared/theme';
import { useFormState } from 'react-hook-form';
import { useStudyContext } from '@vivli/features/studies/infrastructure/context';
import { useAdminStudyStatusChange } from '@vivli/features/studies/infrastructure/hook';
import { useModalApiContext } from '@vivli/core/infrastructure/context';
import { useNavigate } from 'react-router';
import { DTISubmitStudyHeader } from '@vivli/shared/infrastructure/constants';
import { AdminHeaderButtonView, AdminHeaderTitleView, AdminHeaderView } from '@vivli/shared/views';

const flexRowStyle: CSSProperties = {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'flex-end'
};
const marginStyle: CSSProperties = {
    marginRight: Size.INSET_SIZE
};
const submitButtonStyle: CSSProperties = {
    ...Styles.Button.BUTTON_DARKER_GREEN_CHECK,
    ...marginStyle
};

interface SubmitStudyHeaderComponentProps {
    studyId: string;
    userCanDelete: () => boolean;
    userCanApprove: () => boolean;
    userCanSave: () => boolean;
}

export const SubmitStudyHeaderComponent = ({ studyId, userCanDelete, userCanApprove, userCanSave }: SubmitStudyHeaderComponentProps) => {
    const { isValid, isDirty } = useFormState();
    const modalService = useModalApiContext();
    const navigate = useNavigate();
    const { study, handleDeleteStudy, handleUpdateAndStatusChange, handleFormSave, isSavingStudy } = useStudyContext();
    const studyStatusChange = useAdminStudyStatusChange(study);
    const statusObject = studyStatusChange.getCurationStatusObject();

    const finalStep = () => {
        modalService.message(
            'Study submitted to Vivli for processing.  You will be notified when the study has been curated and posted to Vivli. You will now be brought to a list of studies in progress.',
            { showBackground: false, showLogo: true, showContinueButton: true }
        );
        navigate('/admin/studies/InProgress');
    };

    const handleSubmit = () => {
        handleUpdateAndStatusChange(statusObject.approveProps, finalStep);
    };

    const isDisabled = !isValid || !isDirty;
    const showWithdrawButton = studyId && userCanDelete();
    const showSubmitButton = studyId && userCanApprove();
    const showSaveButton = studyId && userCanSave();

    return (
        <AdminHeaderView>
            <div style={flexRowStyle}>
                <div style={{ ...leftHeaderContainerStyle, paddingBottom: 10, flexDirection: 'column' }}>
                    <GoBackLinkComponent destination={'/admin/studies/Draft'} style={{ justifyContent: 'left' }} />
                    <AdminHeaderTitleView title={'Submit New Study'} />
                </div>
                {showWithdrawButton && (
                    <AdminHeaderButtonView
                        title={'Withdraw Submission'}
                        onClick={handleDeleteStudy}
                        isLoading={isSavingStudy}
                        style={marginStyle}
                        className={'submitStudy_withdraw'}
                        dataId={DTISubmitStudyHeader.SubmitStudyWithdraw}
                    />
                )}
                {showSubmitButton && (
                    <AdminHeaderButtonView
                        title={'Submit for Curation'}
                        onClick={handleSubmit}
                        isLoading={isSavingStudy}
                        isDisabled={!isValid}
                        style={submitButtonStyle}
                        className={'submitStudy_submit'}
                        dataId={DTISubmitStudyHeader.SubmitStudySubmit}
                    />
                )}
                {showSaveButton && (
                    <AdminHeaderButtonView
                        title={'Save Draft'}
                        onClick={() => handleFormSave()}
                        isLoading={isSavingStudy}
                        isDisabled={isDisabled}
                        className={'submitStudy_save'}
                        dataId={DTISubmitStudyHeader.SubmitStudySave}
                    />
                )}
            </div>
        </AdminHeaderView>
    );
};
